import * as React from 'react';
import Logo from '@doordash/component-logo';
import { Inset, StackChildren, Text, Stack, StackChildrenSize, InlineChildren, TextDecoration, TextDisplayMode, } from '@doordash/design-language';
import Button from '@doordash/component-button';
import Separator, { SeparatorType } from '@doordash/component-separator';
import { useLocation, useParams } from 'react-router-dom';
import * as queryString from 'query-string';
import { useDVSBoolean } from '@doordash/lib-dvs-client/lib/react';
import { useGetConsumerInfo } from '../../../core/DynamicValues/hook';
import { useClaimVoucher } from '../../../hooks/useClaimVoucher';
import { IMAGE_BASE_URL, HOME_PAGE_PATH, PRODUCTION_BASE_URL, HELP_URL, } from '../../../constants/values';
import { ContentWrapper, HelpLink, VoucherDetails, VoucherGuides, ImgContainer, PageContainer, } from './AcceptVoucherStyles';
import { getVoucherValidPeriod } from '../utils/voucherDateTimeHelper';
import i18n from '../../../i18n';
import { CurrencyCode, formatMoney } from '@doordash/lib-i18n';
import LocationPathHelper from '../../../utils/location_path_helper';
import { BENEFIT_ACTIVATION_BENEFIT_TYPE, DDFB_IDENTITY_LAYOUTS, getIdentityUrl, } from '../../../utils/identity_helper';
import Loading from '@doordash/component-loading';
import VoucherErrorPage from './VoucherErrorPage';
import VoucherStrings from '../VoucherStrings';
import track from '../../../utils/track';
import { VoucherParticipantEvents } from '../../../constants/events/names';
import { QUERY_PARAMS } from '../../../constants/queryParams';
import { DV2_NAMES, dv2Defaults } from '../../../core/DV2';
import { usePreviewVoucher } from '../../../hooks/usePreviewVoucherRedemption';
var pageStrings = VoucherStrings.acceptVoucherPage;
var HowItWorksImageUrl = IMAGE_BASE_URL + "/s/managed/consumer/teams/voucher-how-it-works-screenshot.png";
var VoucherDetailField = function (_a) {
    var label = _a.label, value = _a.value;
    return (React.createElement(Inset, { verticalSize: Inset.Sizes.None, horizontalSize: Inset.Sizes.None },
        React.createElement(Stack, { size: Stack.Sizes.XSmall },
            React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary }, label),
            React.createElement(Text, { styles: Text.Styles.Body1, color: Text.Colors.TextPrimary }, value)),
        React.createElement(Separator, { type: SeparatorType.FullBorder })));
};
export var AcceptVoucher = function () {
    var _a, _b, _c;
    var _d = useParams(), teamAccountId = _d.teamAccountId, budgetId = _d.budgetId;
    var searchParams = queryString.parse(useLocation().search);
    var utmCampaignId = (searchParams === null || searchParams === void 0 ? void 0 : searchParams[QUERY_PARAMS.ITERABLE_CAMPAIGN]) || '';
    var voucherCode = (searchParams === null || searchParams === void 0 ? void 0 : searchParams[QUERY_PARAMS.VOUCHER_CODE]) || '';
    var isVouchersV2Enabled = useDVSBoolean(DV2_NAMES.ddfwVouchersV2, dv2Defaults.ddfwVouchersV2);
    var _e = usePreviewVoucher(budgetId, teamAccountId, true), previewVoucherData = _e.data, isPreviewVoucherLoading = _e.loading, previewVoucherError = _e.error; // setting skipUserValidation to true, since we only need voucher info
    var _f = useGetConsumerInfo(), isGuest = _f.isGuest, consumerInfoLoading = _f.loading, consumerError = _f.error;
    var currency = CurrencyCode.USD;
    var userLocale = i18n.getCurrentLocale();
    var voucherAllotmentDisplay = function (voucherValue) {
        return formatMoney({
            currency: currency,
            userLocale: userLocale,
            unitAmount: voucherValue,
            concise: true,
        });
    };
    var _g = useClaimVoucher(), claimVoucher = _g.claimVoucher, isVoucherLoading = _g.isLoading, voucher = _g.voucher, teamName = _g.teamName, claimVoucherError = _g.error;
    // guest should log in DD before accepting a voucher
    React.useEffect(function () {
        var _a;
        if (((!consumerInfoLoading && isGuest) || consumerError) &&
            !isPreviewVoucherLoading &&
            ((_a = previewVoucherData === null || previewVoucherData === void 0 ? void 0 : previewVoucherData.previewVoucherRedemption) === null || _a === void 0 ? void 0 : _a.teamName)) {
            // user is a guest, voucher team name should still exist
            track(VoucherParticipantEvents.ACCEPT_VOUCHER_PAGE_LOAD, {
                voucher_id: budgetId,
                is_logged_in: false,
                dd_campaign_id: utmCampaignId,
            });
            // propagate url params, this may also contain the voucher code
            var existingParams = LocationPathHelper.getWindowLocationSearch() || '';
            var nextPath = "" + (LocationPathHelper.getPathName() || HOME_PAGE_PATH) + existingParams;
            var identityLayout = isVouchersV2Enabled
                ? DDFB_IDENTITY_LAYOUTS.BENEFIT_ACTIVATION
                : DDFB_IDENTITY_LAYOUTS.CONSUMER_WEB;
            var additionalParams = isVouchersV2Enabled
                ? {
                    benefitType: BENEFIT_ACTIVATION_BENEFIT_TYPE.Voucher,
                    companyName: previewVoucherData.previewVoucherRedemption.teamName || '',
                }
                : {};
            window.location.href = getIdentityUrl({ nextPath: nextPath }, {
                identityMode: 'login',
            }, additionalParams, identityLayout);
        }
    }, [
        isGuest,
        consumerInfoLoading,
        consumerError,
        budgetId,
        utmCampaignId,
        isPreviewVoucherLoading,
        previewVoucherData,
        voucherCode,
        isVouchersV2Enabled,
    ]);
    React.useEffect(function () {
        if (budgetId && teamAccountId && !consumerInfoLoading && !isGuest) {
            track(VoucherParticipantEvents.ACCEPT_VOUCHER_PAGE_LOAD, {
                voucher_id: budgetId,
                is_logged_in: true,
                dd_campaign_id: utmCampaignId,
            });
            claimVoucher({
                voucherId: budgetId,
                teamAccountId: teamAccountId,
                voucherCode: voucherCode,
                enableEmailNotifications: false,
            }, {
                onCompleted: function (data) {
                    var _a;
                    if (((_a = data.claimVoucher) === null || _a === void 0 ? void 0 : _a.error) === null) {
                        track(VoucherParticipantEvents.ACCEPT_VOUCHER_SUCCESS, {
                            voucher_id: budgetId,
                            dd_campaign_id: utmCampaignId,
                        });
                    }
                },
            });
        }
    }, [isGuest, consumerInfoLoading, teamAccountId, budgetId]); // eslint-disable-line react-hooks/exhaustive-deps
    // if the user is guest, show loading until the page gets redirected to avoid page flickering
    if (consumerInfoLoading ||
        isPreviewVoucherLoading ||
        isVoucherLoading ||
        isGuest) {
        return React.createElement(Loading, null);
    }
    if (previewVoucherError ||
        ((_a = previewVoucherData === null || previewVoucherData === void 0 ? void 0 : previewVoucherData.previewVoucherRedemption) === null || _a === void 0 ? void 0 : _a.error) ||
        claimVoucherError) {
        var error = ((_b = previewVoucherData === null || previewVoucherData === void 0 ? void 0 : previewVoucherData.previewVoucherRedemption) === null || _b === void 0 ? void 0 : _b.error) || claimVoucherError;
        return React.createElement(VoucherErrorPage, { error: error });
    }
    return (React.createElement(PageContainer, null,
        React.createElement(Inset, { size: Inset.Sizes.XLarge },
            React.createElement(Logo, { color: Logo.Colors.Default, size: Logo.Sizes.Large })),
        React.createElement(ContentWrapper, null,
            React.createElement(Text, { styles: Text.Styles.PageTitle2, textAlign: Text.TextAlignments.Center }, pageStrings.title()),
            React.createElement(VoucherDetails, null,
                React.createElement(Text, { styles: Text.Styles.Label1Emphasis }, pageStrings.voucherDetails()),
                React.createElement(VoucherDetailField, { label: pageStrings.voucherName(), value: (voucher === null || voucher === void 0 ? void 0 : voucher.name) || '' }),
                React.createElement(VoucherDetailField, { label: pageStrings.voucherIssuer(), value: teamName }),
                React.createElement(VoucherDetailField, { label: pageStrings.voucherValue(), value: voucherAllotmentDisplay(((_c = voucher === null || voucher === void 0 ? void 0 : voucher.maximumAllotment) === null || _c === void 0 ? void 0 : _c.unitAmount) || 0) }),
                React.createElement(VoucherDetailField, { label: pageStrings.voucherValidFor(), value: voucher ? getVoucherValidPeriod(voucher) : '' }),
                React.createElement(Button, { size: Button.Sizes.Medium, onClick: function () {
                        window.location.href = PRODUCTION_BASE_URL;
                    } }, pageStrings.confirmationButton())),
            React.createElement(VoucherGuides, null,
                React.createElement(InlineChildren, null,
                    React.createElement(StackChildren, null,
                        React.createElement(StackChildren, { size: StackChildrenSize.XxxSmall },
                            React.createElement(Text, { styles: Text.Styles.Label1Emphasis }, pageStrings.guideTitle1()),
                            React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary }, pageStrings.guideStep1()),
                            React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary }, pageStrings.guideStep2()),
                            React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary }, pageStrings.guideStep3())),
                        React.createElement(StackChildren, { size: StackChildrenSize.XxxSmall },
                            React.createElement(Text, { styles: Text.Styles.Label1Emphasis }, pageStrings.guideTitle2()),
                            React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary, display: TextDisplayMode.InlineBlock },
                                'Contact our VIP support team for any live order questions at ',
                                React.createElement(Text, { textDecoration: TextDecoration.Underline, styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary, display: TextDisplayMode.InlineBlock }, pageStrings.supportNumber()),
                                React.createElement(Text, { styles: Text.Styles.Caption1, color: Text.Colors.TextSecondary, display: TextDisplayMode.Inline }, '. You can also visit our '),
                                React.createElement(HelpLink, { href: HELP_URL, target: "_blank", rel: "noopener noreferrer" }, pageStrings.helpCenterLink()),
                                "."))),
                    React.createElement(ImgContainer, { src: HowItWorksImageUrl }))))));
};
export default AcceptVoucher;
